import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, output, signal, viewChild } from '@angular/core';

interface Point {
  x: number;
  y: number;
}

interface Ghost extends Point {
  direction: Point;
  color: string;
}

type GameStatus = 'playing' | 'won' | 'lost';

const COLS = 15;
const ROWS = 11;
const CELL = 20;
const TICK_MS = 180;
const PLAYER_START: Point = { x: 7, y: 9 };
const GHOST_STARTS: Array<{ point: Point; color: string }> = [
  { point: { x: 6, y: 1 }, color: '#ff4d4d' },
  { point: { x: 8, y: 1 }, color: '#4dd2ff' }
];

// Isolated interior pillars (border walls are added implicitly). Kept sparse
// so the maze always stays fully connected.
const INNER_WALLS: Point[] = [
  { x: 3, y: 3 }, { x: 4, y: 3 }, { x: 10, y: 3 }, { x: 11, y: 3 },
  { x: 3, y: 7 }, { x: 4, y: 7 }, { x: 10, y: 7 }, { x: 11, y: 7 },
  { x: 6, y: 5 }, { x: 7, y: 5 }, { x: 8, y: 5 },
  { x: 7, y: 2 }, { x: 7, y: 8 }
];

const DIRECTIONS: Point[] = [
  { x: 0, y: -1 },
  { x: 0, y: 1 },
  { x: -1, y: 0 },
  { x: 1, y: 0 }
];

/**
 * Hidden "PACMAN.EXE" easter egg: a simplified Pac-Man on a small maze,
 * styled to match the IBM CRT terminal theme. Only reachable while
 * terminal mode is active, triggered by typing "pacman" (see App.handleGlobalKeydown).
 */
@Component({
  selector: 'app-pacman-game',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown)': 'onKeydown($event)'
  },
  template: `
    <div class="pacman-panel">
      <div class="pacman-header">
        <span class="pacman-title">*** PACMAN.EXE ***</span>
        <span class="pacman-score">SCORE: {{ score() }}</span>
        <button class="pacman-close" type="button" (click)="requestClose()">[ESC] CLOSE</button>
      </div>
      <canvas #canvasEl [width]="canvasWidth" [height]="canvasHeight" class="pacman-canvas"></canvas>
      @if (status() !== 'playing') {
        <div class="pacman-gameover">{{ status() === 'won' ? 'YOU CLEARED THE MAZE!' : 'GAME OVER' }} — SCORE {{ score() }} — PRESS ENTER TO RESTART</div>
      } @else {
        <div class="pacman-hint">USE ARROW KEYS / WASD TO MOVE</div>
      }
    </div>
  `,
  styles: [`
    :host {
      position: fixed;
      left: 0;
      right: 0;
      bottom: 0;
      height: 50vh;
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.94);
      border-top: 2px solid #33ff66;
      font-family: 'IBM Plex Mono', 'Courier New', monospace;
    }
    .pacman-panel {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.75rem;
    }
    .pacman-header {
      display: flex;
      gap: 1.5rem;
      align-items: center;
      color: #33ff66;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      font-size: 0.875rem;
    }
    .pacman-score {
      color: #ffb000;
    }
    .pacman-close {
      background: none;
      border: 1px solid #33ff66;
      color: #33ff66;
      padding: 0.25rem 0.6rem;
      cursor: pointer;
      font-family: inherit;
      font-size: 0.75rem;
      border-radius: 0;
    }
    .pacman-close:hover {
      background: #33ff66;
      color: #000000;
    }
    .pacman-canvas {
      border: 1px solid #1a4d1a;
      image-rendering: pixelated;
      box-shadow: 0 0 24px rgba(51, 255, 102, 0.25);
    }
    .pacman-gameover {
      color: #ffb000;
      font-size: 0.875rem;
      letter-spacing: 0.05em;
      text-align: center;
      animation: pacman-blink 1s steps(1) infinite;
    }
    .pacman-hint {
      color: rgba(51, 255, 102, 0.5);
      font-size: 0.75rem;
      letter-spacing: 0.05em;
      text-align: center;
    }
    @keyframes pacman-blink {
      50% { opacity: 0; }
    }
  `]
})
export class PacmanGame implements OnInit, OnDestroy {
  private readonly canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('canvasEl');

  close = output<void>();

  score = signal(0);
  status = signal<GameStatus>('playing');

  readonly canvasWidth = COLS * CELL;
  readonly canvasHeight = ROWS * CELL;

  private ctx?: CanvasRenderingContext2D;
  private intervalId?: ReturnType<typeof setInterval>;

  private player: Point = { ...PLAYER_START };
  private direction: Point = { x: 0, y: 0 };
  private nextDirection: Point = { x: 0, y: 0 };
  private mouthOpen = true;
  private ghosts: Ghost[] = this.buildGhosts();
  private dots: Set<string> = this.buildDots();

  ngOnInit(): void {
    this.ctx = this.canvasRef().nativeElement.getContext('2d') ?? undefined;
    this.startLoop();
  }

  ngOnDestroy(): void {
    this.stopLoop();
  }

  onKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowUp':
      case 'w':
      case 'W':
        this.nextDirection = { x: 0, y: -1 };
        break;
      case 'ArrowDown':
      case 's':
      case 'S':
        this.nextDirection = { x: 0, y: 1 };
        break;
      case 'ArrowLeft':
      case 'a':
      case 'A':
        this.nextDirection = { x: -1, y: 0 };
        break;
      case 'ArrowRight':
      case 'd':
      case 'D':
        this.nextDirection = { x: 1, y: 0 };
        break;
      case 'Escape':
        this.requestClose();
        break;
      case 'Enter':
        if (this.status() !== 'playing') {
          this.restart();
        }
        break;
      default:
        return;
    }
    event.preventDefault();
  }

  requestClose(): void {
    this.close.emit();
  }

  private startLoop(): void {
    this.stopLoop();
    this.intervalId = setInterval(() => this.tick(), TICK_MS);
    this.draw();
  }

  private stopLoop(): void {
    if (this.intervalId !== undefined) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
  }

  private restart(): void {
    this.player = { ...PLAYER_START };
    this.direction = { x: 0, y: 0 };
    this.nextDirection = { x: 0, y: 0 };
    this.ghosts = this.buildGhosts();
    this.dots = this.buildDots();
    this.score.set(0);
    this.status.set('playing');
    this.startLoop();
  }

  private tick(): void {
    this.mouthOpen = !this.mouthOpen;

    if (this.canMove(this.player.x + this.nextDirection.x, this.player.y + this.nextDirection.y)) {
      this.direction = { ...this.nextDirection };
    }
    const prevPlayer = { ...this.player };
    const nextPlayer = { x: this.player.x + this.direction.x, y: this.player.y + this.direction.y };
    if (this.canMove(nextPlayer.x, nextPlayer.y)) {
      this.player = nextPlayer;
    }

    // Player walked into a ghost's cell.
    if (this.ghosts.some(ghost => this.samePoint(ghost, this.player))) {
      this.loseGame();
      return;
    }

    const dotKey = `${this.player.x},${this.player.y}`;
    if (this.dots.has(dotKey)) {
      this.dots.delete(dotKey);
      this.score.update(s => s + 10);
      if (this.dots.size === 0) {
        this.status.set('won');
        this.stopLoop();
        this.draw();
        return;
      }
    }

    for (const ghost of this.ghosts) {
      const prevGhost = { x: ghost.x, y: ghost.y };
      this.moveGhost(ghost);
      // Ghost stepped onto the player, or player and ghost swapped cells (passed through each other).
      const caught = this.samePoint(ghost, this.player) ||
        (this.samePoint(ghost, prevPlayer) && this.samePoint(prevGhost, this.player));
      if (caught) {
        this.loseGame();
        return;
      }
    }

    this.draw();
  }

  private loseGame(): void {
    this.status.set('lost');
    this.stopLoop();
    this.draw();
  }

  private samePoint(a: Point, b: Point): boolean {
    return a.x === b.x && a.y === b.y;
  }

  private isGhostFree(ghost: Ghost, x: number, y: number): boolean {
    return this.canMove(x, y) && !this.ghosts.some(other => other !== ghost && other.x === x && other.y === y);
  }

  private moveGhost(ghost: Ghost): void {
    // Ghosts block each other, so they never stack or pass through one another.
    const nonReverse = DIRECTIONS.filter(d =>
      this.isGhostFree(ghost, ghost.x + d.x, ghost.y + d.y) &&
      !(d.x === -ghost.direction.x && d.y === -ghost.direction.y)
    );
    const allValid = DIRECTIONS.filter(d => this.isGhostFree(ghost, ghost.x + d.x, ghost.y + d.y));
    const candidates = nonReverse.length > 0 ? nonReverse : allValid;
    if (candidates.length === 0) {
      return;
    }

    let chosen: Point;
    if (Math.random() < 0.2) {
      chosen = candidates[Math.floor(Math.random() * candidates.length)];
    } else {
      chosen = candidates.reduce((best, d) => {
        const dist = Math.abs(ghost.x + d.x - this.player.x) + Math.abs(ghost.y + d.y - this.player.y);
        const bestDist = Math.abs(ghost.x + best.x - this.player.x) + Math.abs(ghost.y + best.y - this.player.y);
        return dist < bestDist ? d : best;
      });
    }

    ghost.direction = chosen;
    ghost.x += chosen.x;
    ghost.y += chosen.y;
  }

  private canMove(x: number, y: number): boolean {
    return x >= 0 && x < COLS && y >= 0 && y < ROWS && !this.isWall(x, y);
  }

  private isWall(x: number, y: number): boolean {
    if (x === 0 || x === COLS - 1 || y === 0 || y === ROWS - 1) {
      return true;
    }
    return INNER_WALLS.some(w => w.x === x && w.y === y);
  }

  private buildGhosts(): Ghost[] {
    return GHOST_STARTS.map(({ point, color }) => ({ ...point, direction: { x: 0, y: 1 }, color }));
  }

  private buildDots(): Set<string> {
    const dots = new Set<string>();
    for (let y = 1; y < ROWS - 1; y++) {
      for (let x = 1; x < COLS - 1; x++) {
        if (!this.isWall(x, y) && !(x === PLAYER_START.x && y === PLAYER_START.y)) {
          dots.add(`${x},${y}`);
        }
      }
    }
    return dots;
  }

  private directionAngle(dir: Point): number {
    if (dir.x === 1) return 0;
    if (dir.x === -1) return Math.PI;
    if (dir.y === 1) return Math.PI / 2;
    if (dir.y === -1) return -Math.PI / 2;
    return 0;
  }

  private draw(): void {
    const ctx = this.ctx;
    if (!ctx) {
      return;
    }

    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);

    // Walls
    ctx.fillStyle = '#063d0f';
    ctx.strokeStyle = '#1a4d1a';
    for (let y = 0; y < ROWS; y++) {
      for (let x = 0; x < COLS; x++) {
        if (this.isWall(x, y)) {
          ctx.fillRect(x * CELL, y * CELL, CELL, CELL);
          ctx.strokeRect(x * CELL + 0.5, y * CELL + 0.5, CELL - 1, CELL - 1);
        }
      }
    }

    // Dots
    ctx.fillStyle = '#ffb000';
    this.dots.forEach(key => {
      const [x, y] = key.split(',').map(Number);
      ctx.beginPath();
      ctx.arc(x * CELL + CELL / 2, y * CELL + CELL / 2, 2.5, 0, Math.PI * 2);
      ctx.fill();
    });

    // Ghosts
    this.ghosts.forEach(ghost => {
      ctx.fillStyle = ghost.color;
      ctx.beginPath();
      ctx.arc(ghost.x * CELL + CELL / 2, ghost.y * CELL + CELL / 2, CELL / 2 - 2, 0, Math.PI * 2);
      ctx.fill();
    });

    // Player (Pac-Man)
    const cx = this.player.x * CELL + CELL / 2;
    const cy = this.player.y * CELL + CELL / 2;
    const radius = CELL / 2 - 2;
    const dirAngle = this.directionAngle(this.direction);
    const mouth = this.mouthOpen ? 0.25 * Math.PI : 0.02 * Math.PI;
    ctx.fillStyle = '#ffe100';
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, radius, dirAngle + mouth, dirAngle - mouth, false);
    ctx.closePath();
    ctx.fill();
  }
}
